import { useEffect, useState } from 'react'

import { MODULE_TABLE } from '@app/app.config'
import { ACTION_MUTATION_PROMISE, PAGE_COUNT, TOASTIFY_DEFAULT_OPTIONS } from '@app/app.constants'
import { useAppDispatch, useAppSelector } from '@app/app.hook'
import { getErrorText } from '@app/app.method'
import { selectActiveModules, selectStrings } from '@app/slices/slice.app'
import { selectToken } from '@app/slices/slice.token'
import { TokenData } from '@app/types/type.token'
import { useInitializeSidebarVisibility } from '@login/MutationProvider/initializeSidebarVisibility'
import { useRevalidateToken } from '@login/MutationProvider/revalidateToken'
import { useValidateAPIPath } from '@login/MutationProvider/validateAPIPath'
import { useValidateRoute } from '@login/MutationProvider/validateRoute'

import _ from 'lodash'
import { toast } from 'react-toastify'

import { selectRouter } from '@app/app.store'
import { useGetPatientsMutation } from '@doc/api'
import AddInterface from '@doc/components/patients/add/AddInterface'
import DataTable from '@doc/components/patients/DataTable'
import { MODULE_VERSION } from '@doc/constants'
import { selectPatientMenu, setPatientMenu } from '@doc/slice'
import { GetPatientsResponse } from '@doc/type'
import DashboardResponsiveHeader from '@fmt/components/DashboardResponsiveHeader'
import { back, push } from '@lagunovsky/redux-react-router'

const PatientsMain = () => {
    const dispatch = useAppDispatch()
    const token = useAppSelector(selectToken)

    const activeModules = useAppSelector(selectActiveModules)
    const strings = useAppSelector(selectStrings)
    const initializeSidebarVisibility = useInitializeSidebarVisibility()

    const patientMenu = useAppSelector(selectPatientMenu)

    const validateRoute = useValidateRoute()
    const revalidateToken = useRevalidateToken()
    const validateAPIPath = useValidateAPIPath()
    const router = useAppSelector(selectRouter)

    const fixedCacheKey = 'shared-getPatients-key'
    const [getPatients, getPatientsMutation] = useGetPatientsMutation({
        fixedCacheKey
    })
    const [currentPage, setCurrentPage] = useState(0)
    const [search, setSearch] = useState<string>('')
    const [pageCount, setPageCount] = useState(PAGE_COUNT[1].value)

    // a workaround to prevent tables from flickering. simple but it works.
    const [
        getPatientsResponse,
        setGetPatientsResponse
    ] = useState<GetPatientsResponse>()

    useEffect(() => {
        if (getPatientsMutation.data) {
            setGetPatientsResponse(getPatientsMutation.data)
        }
    }, [getPatientsMutation.data])

    useEffect(() => {
        initializeSidebarVisibility(true)
    }, [])

    useEffect(() => {
        setCurrentPage(0)
    }, [pageCount])

    const unsubscribeGetPatients = () => {
        const unsubscribeMutation = getPatients({
            data: {}
        } as any)
        unsubscribeMutation.abort()
        unsubscribeMutation.unsubscribe()
    }

    /** create fetch data function */
    const fetchData = (token: TokenData) => {
        /** this will reset the data to unInitialized AND prevent sending a request
             * to the server.
             */
        unsubscribeGetPatients()

        let getPatientsPromise = _.cloneDeep(ACTION_MUTATION_PROMISE)
        let isMounted = true

        const call = async () => {
            if (token.valid) {
                const newToken = await revalidateToken({
                    value: token.value,
                    id: token.id
                }, token.mode)
                if (isMounted) {
                    const isValid = validateAPIPath(
                        activeModules.arr,
                        MODULE_TABLE.doc.moduleName,
                        MODULE_TABLE.doc.apiPaths
                            .getPatients.path,
                        true
                    )

                    if (isValid && newToken.value) {
                        const skip = currentPage * (getPatientsResponse
                            ?.data.limit || 1)

                        const totalRecords = (getPatientsResponse
                            ?.data.totalRecords || 0) - 1
                        getPatientsPromise = getPatients({
                            authToken: newToken.value,
                            data: {
                                skip: skip > (getPatientsResponse?.data
                                    .totalRecords || 0)
                                    ? totalRecords
                                    : skip,
                                // limit: getPatientsResponse?.data.limit,
                                // will use page selector now
                                limit: pageCount,
                                search
                            }
                        })
                    }
                }
            }
        }

        call()

        return () => {
            isMounted = false
            getPatientsPromise && getPatientsPromise.abort()
        }
    }

    useEffect(() => {
        return fetchData(token)
    }, [token.id, token.valid, currentPage, search, pageCount])

    useEffect(() => {
        if (getPatientsMutation.error) {
            const message = getErrorText(getPatientsMutation.error)
            console.error(message)
            toast.error(message, { ...TOASTIFY_DEFAULT_OPTIONS })
        }
    }, [getPatientsMutation.error])

    // a useEffect where the addModal is open when the route is /add.
    useEffect(() => {
        if ((router.location.pathname === MODULE_TABLE.doc
            .routes.createPatient)) {
            dispatch(setPatientMenu({
                ...patientMenu,
                showAddModal: true
            }))
        }
        // do not set to false automatically.let it close through
        // another useEffect
    }, [router.location.pathname])

    useEffect(() => {
        if (!patientMenu.showAddModal) {
            const isValid = validateRoute(
                activeModules.arr,
                MODULE_TABLE.doc.moduleName,
                MODULE_TABLE.doc.routes.patients,
                true
            )

            if (isValid) {
                dispatch(push(isValid.route))
            }
        }
    }, [patientMenu.showAddModal])

    return <div className={'patient-page'}>

        {/* dashboard responsive header */}
        <DashboardResponsiveHeader />

        <div className={'main-content'}>

            <div className={'container-fluid mx-auto'}>
                <div className={'row justify-content-between pt-16 pb-0 px-5'}>
                    <div className={'col-auto'}>
                        <a onClick={(e) => {
                            e.preventDefault()
                            dispatch(back())
                        }} className={'btn btn-round btn-sm-md'}>
                            <i
                                className={'fa-light fa-arrow-left'}
                                aria-hidden={'true'}>
                            </i>
                        </a>
                    </div>
                    <div className={'col-auto'}>
                        <h1 className={'my-0'}>{strings.doc
                            ?.text.patient.patient_records.patient}</h1>
                    </div>
                    <div className={'col-auto'}>
                        <a href={'#'}
                            onClick={(e) => {
                                e.preventDefault()
                            }} className={[
                                'btn btn-round btn-sm-md invisible'
                            ].join(' ')}>
                            <i
                                className={'fa-light fa-arrow-right'}
                                aria-hidden={'true'}
                            >
                            </i>
                        </a>
                    </div>
                </div>
                <div className={'row justify-content-between mt-5'}>
                    <div className={'col'}>
                        <div className={
                            'container-fluid px-5 px-sm-3 patient-body list-interface'
                        }>
                            <div className={'h-100 row'}>
                                <div className={' col-12 col-md-11 mx-auto'}>
                                    <DataTable
                                        search={search}
                                        pageCount={pageCount}
                                        setPageCount={setPageCount}
                                        setSearch={setSearch}
                                        mode={'EDIT'}
                                        getPatientsMutation={{
                                            isLoading: getPatientsMutation.isLoading,
                                            isSuccess: getPatientsMutation.isSuccess,
                                            responseData: getPatientsResponse
                                        }}
                                        currentPage={currentPage}
                                        setCurrentPage={setCurrentPage}
                                        fixedCacheKey={fixedCacheKey}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <AddInterface
                    fetchData={fetchData}
                />

            </div>

        </div>

        <div
            className={'position-fixed bottom-0 end-0 pe-5 fs-label fw-light version-text'}
        >
            {MODULE_VERSION}
        </div>
    </div>
}

export default PatientsMain
